'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function TimeCardCalculator() {
  const t = useTranslations("tools.timeCardCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Time card entries state
  const [timeEntries, setTimeEntries] = useState([
    { day: 'Monday', start: '8:30AM', end: '5:00PM', break: '0:30' },
    { day: 'Tuesday', start: '', end: '', break: '' },
    { day: 'Wednesday', start: '', end: '', break: '' },
    { day: 'Thursday', start: '', end: '', break: '' },
    { day: 'Friday', start: '', end: '', break: '' },
    { day: 'Saturday', start: '', end: '', break: '' },
    { day: 'Sunday', start: '', end: '', break: '' },
  ]);

  // Options state
  const [roundTime, setRoundTime] = useState('');
  const [includePayment, setIncludePayment] = useState(false);
  const [hourlyRate, setHourlyRate] = useState(35);
  const [overtimeType, setOvertimeType] = useState('none');
  const [dailyHours, setDailyHours] = useState(8);
  const [weeklyHours, setWeeklyHours] = useState(40);
  const [overtimeRate, setOvertimeRate] = useState(1.5);

  // Results state
  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    secondary: '#22c55e',
    danger: '#ef4444',
    dangerHover: '#dc2626',
    textPrimary: '#1e293b',
    textSecondary: '#64748b',
    border: '#e2e8f0',
    background: '#f8fafc',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem' },
      { padding: '3rem 1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      {
        maxWidth: '1000px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    toolHeader: {
      textAlign: 'center' as const,
      marginBottom: '2rem',
    },
    h2: getResponsiveStyle(
      { color: colors.textPrimary, fontWeight: 700, marginBottom: '0.5rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    toolDescription: getResponsiveStyle(
      { color: colors.textSecondary, marginBottom: '2rem', textAlign: 'center' as const },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    tableContainer: {
      width: '100%',
      overflowX: 'auto' as const,
      marginBottom: '2rem',
    },
    table: getResponsiveStyle(
      { width: '100%', borderCollapse: 'collapse' as const },
      { minWidth: '600px' },
      {},
      {}
    ),
    th: getResponsiveStyle(
      {
        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
        color: 'white',
        textAlign: 'left' as const,
        fontWeight: 600,
      },
      { padding: '0.75rem 0.5rem', fontSize: '0.85rem' },
      { padding: '0.875rem 0.75rem', fontSize: '0.9rem' },
      { padding: '1rem', fontSize: '0.9rem' }
    ),
    td: getResponsiveStyle(
      { borderBottom: `1px solid ${colors.border}` },
      { padding: '0.5rem 0.25rem' },
      { padding: '0.625rem 0.5rem' },
      { padding: '0.75rem 1rem' }
    ),
    dayLabel: {
      fontWeight: 600,
      color: '#334155',
      minWidth: '80px',
    },
    timeInput: getResponsiveStyle(
      {
        width: '100%',
        border: `2px solid ${colors.border}`,
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
      },
      { padding: '0.4rem', fontSize: '0.85rem' },
      { padding: '0.45rem', fontSize: '0.875rem' },
      { padding: '0.5rem', fontSize: '0.9rem' }
    ),
    clearBtn: getResponsiveStyle(
      {
        background: colors.danger,
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        fontWeight: 500,
      },
      { padding: '0.35rem 0.6rem', fontSize: '0.8rem' },
      { padding: '0.375rem 0.7rem', fontSize: '0.825rem' },
      { padding: '0.4rem 0.8rem', fontSize: '0.85rem' }
    ),
    actionLinks: getResponsiveStyle(
      {
        textAlign: 'center' as const,
        marginBottom: '1.5rem',
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap' as const,
      },
      { gap: '1rem', flexDirection: 'column' as const },
      { gap: '1.25rem', flexDirection: 'row' as const },
      { gap: '1.5rem', flexDirection: 'row' as const }
    ),
    actionLink: {
      color: colors.primary,
      textDecoration: 'none',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'color 0.3s ease',
    },
    optionsSection: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
        borderRadius: '0.75rem',
        marginBottom: '1.5rem',
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    formGroup: {
      marginBottom: '1.25rem',
    },
    label: getResponsiveStyle(
      {
        display: 'block',
        fontWeight: 600,
        color: '#334155',
        marginBottom: '0.5rem',
      },
      { fontSize: '0.85rem' },
      { fontSize: '0.875rem' },
      { fontSize: '0.9rem' }
    ),
    select: getResponsiveStyle(
      {
        width: '100%',
        border: `2px solid #cbd5e1`,
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
      },
      { padding: '0.55rem', fontSize: '0.85rem' },
      { padding: '0.6rem', fontSize: '0.875rem' },
      { padding: '0.65rem', fontSize: '0.9rem' }
    ),
    input: getResponsiveStyle(
      {
        width: '100%',
        border: `2px solid #cbd5e1`,
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
      },
      { padding: '0.55rem', fontSize: '0.85rem' },
      { padding: '0.6rem', fontSize: '0.875rem' },
      { padding: '0.65rem', fontSize: '0.9rem' }
    ),
    checkboxGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginBottom: '1rem',
    },
    checkbox: {
      width: '18px',
      height: '18px',
      cursor: 'pointer',
    },
    paymentOptions: getResponsiveStyle(
      {
        marginTop: '1rem',
        background: 'white',
        borderRadius: '0.5rem',
        border: '2px solid #cbd5e1',
      },
      { padding: '0.75rem' },
      { padding: '0.875rem' },
      { padding: '1rem' }
    ),
    radioGroup: {
      marginBottom: '0.75rem',
    },
    radioLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      cursor: 'pointer',
      fontWeight: 400,
    },
    inlineInput: {
      width: '60px',
      padding: '0.3rem',
      border: '1px solid #cbd5e1',
      borderRadius: '0.25rem',
      textAlign: 'center' as const,
    },
    buttonGroup: getResponsiveStyle(
      {
        textAlign: 'center' as const,
        marginTop: '2rem',
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap' as const,
      },
      { gap: '0.75rem', flexDirection: 'column' as const },
      { gap: '1rem', flexDirection: 'row' as const },
      { gap: '1rem', flexDirection: 'row' as const }
    ),
    calculateBtn: getResponsiveStyle(
      {
        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
        color: 'white',
        border: 'none',
        borderRadius: '0.75rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
      },
      { padding: '0.875rem 2rem', fontSize: '1rem', width: '100%' },
      { padding: '0.95rem 2.5rem', fontSize: '1.05rem', width: 'auto' },
      { padding: '1rem 3rem', fontSize: '1.1rem', width: 'auto' }
    ),
    clearFormBtn: getResponsiveStyle(
      {
        background: '#64748b',
        color: 'white',
        border: 'none',
        borderRadius: '0.75rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem', width: '100%' },
      { padding: '0.95rem 1.75rem', fontSize: '1.05rem', width: 'auto' },
      { padding: '1rem 2rem', fontSize: '1.1rem', width: 'auto' }
    ),
    results: getResponsiveStyle(
      {
        marginTop: '2rem',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        borderRadius: '0.75rem',
        border: '2px solid #86efac',
      },
      { padding: '1.25rem' },
      { padding: '1.625rem' },
      { padding: '2rem' }
    ),
    resultsH3: getResponsiveStyle(
      {
        color: '#166534',
        margin: '0 0 1.5rem 0',
        textAlign: 'center' as const,
      },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' },
      { fontSize: '1.5rem' }
    ),
    resultsTable: {
      width: '100%',
      marginBottom: '1.5rem',
    },
    resultsTh: getResponsiveStyle(
      {
        background: colors.secondary,
        color: 'white',
        textAlign: 'left' as const,
        fontWeight: 600,
      },
      { padding: '0.5rem', fontSize: '0.8rem' },
      { padding: '0.625rem', fontSize: '0.85rem' },
      { padding: '0.75rem', fontSize: '0.9rem' }
    ),
    resultsTd: getResponsiveStyle(
      {
        borderBottom: '1px solid #bbf7d0',
      },
      { padding: '0.5rem', fontSize: '0.85rem' },
      { padding: '0.625rem', fontSize: '0.9rem' },
      { padding: '0.75rem', fontSize: '0.95rem' }
    ),
    totalRow: getResponsiveStyle(
      {
        background: '#dcfce7',
        fontWeight: 700,
      },
      { fontSize: '1rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.1rem' }
    ),
    resultSummary: getResponsiveStyle(
      {
        display: 'grid',
        gap: '1rem',
        marginTop: '1.5rem',
      },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(2, 1fr)' },
      { gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }
    ),
    summaryCard: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '0.5rem',
        textAlign: 'center' as const,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      },
      { padding: '1rem' },
      { padding: '1.125rem' },
      { padding: '1.25rem' }
    ),
    summaryLabel: getResponsiveStyle(
      {
        color: colors.textSecondary,
        marginBottom: '0.5rem',
      },
      { fontSize: '0.8rem' },
      { fontSize: '0.825rem' },
      { fontSize: '0.85rem' }
    ),
    summaryValue: getResponsiveStyle(
      {
        color: '#166534',
        fontWeight: 700,
      },
      { fontSize: '1.5rem' },
      { fontSize: '1.625rem' },
      { fontSize: '1.75rem' }
    ),
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      {
        color: colors.textSecondary,
        lineHeight: 1.7,
        marginBottom: '1rem',
      },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      {
        color: colors.textSecondary,
        lineHeight: 1.7,
        marginLeft: '1.5rem',
        marginBottom: '1rem',
      },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.75rem',
    },
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: getResponsiveStyle(
      {
        fontWeight: 600,
        color: colors.textPrimary,
        marginBottom: '0.5rem',
      },
      { fontSize: '1.05rem' },
      { fontSize: '1.1rem' },
      { fontSize: '1.15rem' }
    ),
    faqAnswer: getResponsiveStyle(
      {
        color: colors.textSecondary,
        lineHeight: 1.6,
      },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1.05rem' }
    ),
    reference: {
      marginBottom: '0.75rem',
    },
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      wordBreak: 'break-word' as const,
    },
  };

  const parseTime = (timeStr: string): number | null => {
    if (!timeStr || timeStr.trim() === '') return null;

    timeStr = timeStr.trim().toUpperCase();
    let hours = 0, minutes = 0;

    const ampmMatch = timeStr.match(/(\d+):?(\d*)\s*(AM|PM)/);
    if (ampmMatch) {
      hours = parseInt(ampmMatch[1]);
      minutes = ampmMatch[2] ? parseInt(ampmMatch[2]) : 0;
      if (ampmMatch[3] === 'PM' && hours !== 12) hours += 12;
      if (ampmMatch[3] === 'AM' && hours === 12) hours = 0;
    } else if (timeStr.includes(':')) {
      const parts = timeStr.split(':');
      hours = parseInt(parts[0]);
      minutes = parseInt(parts[1]);
    } else if (timeStr.includes('.')) {
      const parts = timeStr.split('.');
      hours = parseInt(parts[0]);
      minutes = Math.round(parseFloat('0.' + parts[1]) * 60);
    } else {
      hours = parseInt(timeStr);
    }

    return hours + minutes / 60;
  };

  const roundTimeValue = (hours: number, roundTo: string): number => {
    if (!roundTo) return hours;
    const roundMinutes = parseInt(roundTo);
    const totalMinutes = hours * 60;
    const rounded = Math.round(totalMinutes / roundMinutes) * roundMinutes;
    return rounded / 60;
  };

  const formatHours = (hours: number): string => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const formatTime = (hours: number): string => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h > 12 ? h - 12 : (h === 0 ? 12 : h);
    return `${displayHour}:${m.toString().padStart(2, '0')} ${period}`;
  };

  const handleInputChange = (index: number, field: string, value: string) => {
    const newEntries = [...timeEntries];
    newEntries[index] = { ...newEntries[index], [field]: value };
    setTimeEntries(newEntries);
  };

  const clearRow = (index: number) => {
    const newEntries = [...timeEntries];
    newEntries[index] = { ...newEntries[index], start: '', end: '', break: '' };
    setTimeEntries(newEntries);
  };

  const copyFirstRow = () => {
    const firstRow = timeEntries[0];
    const newEntries = timeEntries.map((entry, index) =>
      index === 0 ? entry : { ...entry, start: firstRow.start, end: firstRow.end, break: firstRow.break }
    );
    setTimeEntries(newEntries);
  };

  const clearAllRows = () => {
    const newEntries = timeEntries.map(entry => ({ ...entry, start: '', end: '', break: '' }));
    setTimeEntries(newEntries);
    setResults(null);
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const dailyData: any[] = [];
    let totalHours = 0;

    timeEntries.forEach((entry) => {
      if (!entry.start || !entry.end) return;

      const start = parseTime(entry.start);
      const end = parseTime(entry.end);
      const breakHours = parseTime(entry.break) || 0;

      if (start === null || end === null) return;

      let workedHours = end - start - breakHours;
      if (workedHours < 0) workedHours += 24;

      workedHours = roundTimeValue(workedHours, roundTime);

      dailyData.push({
        day: entry.day,
        start: formatTime(start),
        end: formatTime(end),
        break: formatHours(breakHours),
        hours: workedHours
      });

      totalHours += workedHours;
    });

    let totalRegularHours = 0;
    let totalOvertimeHours = 0;
    let totalPay = 0;

    const processedData = dailyData.map(data => {
      let regularHours = data.hours;
      let overtimeHours = 0;
      let dayPay = 0;

      if (includePayment && overtimeType !== 'none') {
        if (overtimeType === 'daily' && data.hours > dailyHours) {
          regularHours = dailyHours;
          overtimeHours = data.hours - dailyHours;
        }

        dayPay = (regularHours * hourlyRate) + (overtimeHours * hourlyRate * overtimeRate);
        totalRegularHours += regularHours;
        totalOvertimeHours += overtimeHours;
      } else if (includePayment) {
        dayPay = data.hours * hourlyRate;
        totalRegularHours += data.hours;
      }

      totalPay += dayPay;

      return { ...data, pay: dayPay };
    });

    if (includePayment && overtimeType === 'weekly' && totalHours > weeklyHours) {
      totalRegularHours = weeklyHours;
      totalOvertimeHours = totalHours - weeklyHours;
      totalPay = (totalRegularHours * hourlyRate) + (totalOvertimeHours * hourlyRate * overtimeRate);
    }

    setResults({
      dailyData: processedData,
      totalHours,
      totalRegularHours,
      totalOvertimeHours,
      totalPay,
    });
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t("title")}</h1>
        <p style={styles.summaryText}>{t("subtitle")}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.h2}>{t("toolTitle")}</h2>
          </div>

          <p style={styles.toolDescription}>
            {t("toolDescription")}
          </p>

          <form onSubmit={handleCalculate}>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>{t("dayColumn")}</th>
                    <th style={styles.th}>{t("startTimeColumn")}</th>
                    <th style={styles.th}>{t("endTimeColumn")}</th>
                    <th style={styles.th}>{t("breakColumn")}</th>
                    <th style={styles.th}>{t("actionColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {timeEntries.map((entry, index) => (
                    <tr key={index} style={{ background: index % 2 === 0 ? 'transparent' : '#f8fafc' }}>
                      <td style={{ ...styles.td, ...styles.dayLabel }}>{t(`days.${entry.day.toLowerCase()}`)}</td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          style={styles.timeInput}
                          placeholder="8:30AM"
                          value={entry.start}
                          onChange={(e) => handleInputChange(index, 'start', e.target.value)}
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          style={styles.timeInput}
                          placeholder="5:00PM"
                          value={entry.end}
                          onChange={(e) => handleInputChange(index, 'end', e.target.value)}
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="text"
                          style={styles.timeInput}
                          placeholder="0:30"
                          value={entry.break}
                          onChange={(e) => handleInputChange(index, 'break', e.target.value)}
                        />
                      </td>
                      <td style={styles.td}>
                        <button
                          type="button"
                          style={styles.clearBtn}
                          onClick={() => clearRow(index)}
                          onMouseEnter={(e) => e.currentTarget.style.background = colors.dangerHover}
                          onMouseLeave={(e) => e.currentTarget.style.background = colors.danger}
                        >
                          {t("clearButton")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={styles.actionLinks}>
              <a
                style={styles.actionLink}
                onClick={copyFirstRow}
                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
              >
                📋 {t("copyFirstRow")}
              </a>
              <a
                style={styles.actionLink}
                onClick={clearAllRows}
                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
              >
                🗑️ {t("clearAllEntries")}
              </a>
            </div>

            <div style={styles.optionsSection}>
              <div style={styles.formGroup}>
                <label htmlFor="round-time" style={styles.label}>{t("roundTimeLabel")}</label>
                <select
                  id="round-time"
                  style={styles.select}
                  value={roundTime}
                  onChange={(e) => setRoundTime(e.target.value)}
                >
                  <option value="">{t("noRounding")}</option>
                  <option value="5">{t("round5Minutes")}</option>
                  <option value="10">{t("round10Minutes")}</option>
                  <option value="15">{t("round15Minutes")}</option>
                  <option value="30">{t("round30Minutes")}</option>
                  <option value="60">{t("round60Minutes")}</option>
                </select>
              </div>

              <div style={styles.checkboxGroup}>
                <input
                  type="checkbox"
                  id="include-payment"
                  style={styles.checkbox}
                  checked={includePayment}
                  onChange={(e) => setIncludePayment(e.target.checked)}
                />
                <label htmlFor="include-payment" style={{ cursor: 'pointer', fontWeight: 500 }}>
                  {t("includePayment")}
                </label>
              </div>

              {includePayment && (
                <div style={styles.paymentOptions}>
                  <div style={styles.formGroup}>
                    <label htmlFor="hourly-rate" style={styles.label}>{t("hourlyRateLabel")}</label>
                    <input
                      type="number"
                      id="hourly-rate"
                      style={styles.input}
                      value={hourlyRate}
                      onChange={(e) => setHourlyRate(parseFloat(e.target.value))}
                      step="0.01"
                      min="0"
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t("overtimePayLabel")}</label>
                    <div style={styles.radioGroup}>
                      <label style={styles.radioLabel}>
                        <input
                          type="radio"
                          name="overtime-type"
                          value="none"
                          checked={overtimeType === 'none'}
                          onChange={(e) => setOvertimeType(e.target.value)}
                        />
                        {t("noOvertime")}
                      </label>
                    </div>
                    <div style={styles.radioGroup}>
                      <label style={styles.radioLabel}>
                        <input
                          type="radio"
                          name="overtime-type"
                          value="daily"
                          checked={overtimeType === 'daily'}
                          onChange={(e) => setOvertimeType(e.target.value)}
                        />
                        {t("overtimeAfter")} <input
                          type="number"
                          style={styles.inlineInput}
                          value={dailyHours}
                          onChange={(e) => setDailyHours(parseFloat(e.target.value))}
                          min="1"
                        /> {t("hoursPerDay")}
                      </label>
                    </div>
                    <div style={styles.radioGroup}>
                      <label style={styles.radioLabel}>
                        <input
                          type="radio"
                          name="overtime-type"
                          value="weekly"
                          checked={overtimeType === 'weekly'}
                          onChange={(e) => setOvertimeType(e.target.value)}
                        />
                        {t("overtimeAfter")} <input
                          type="number"
                          style={styles.inlineInput}
                          value={weeklyHours}
                          onChange={(e) => setWeeklyHours(parseFloat(e.target.value))}
                          min="1"
                        /> {t("hoursPerWeek")}
                      </label>
                    </div>
                  </div>

                  {overtimeType !== 'none' && (
                    <div style={styles.formGroup}>
                      <label htmlFor="overtime-rate" style={styles.label}>{t("overtimeRateLabel")}</label>
                      <input
                        type="number"
                        id="overtime-rate"
                        style={styles.input}
                        value={overtimeRate}
                        onChange={(e) => setOvertimeRate(parseFloat(e.target.value))}
                        step="0.1"
                        min="1"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={styles.buttonGroup}>
              <button
                type="submit"
                style={styles.calculateBtn}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {t("calculateButton")}
              </button>
              <button
                type="button"
                style={styles.clearFormBtn}
                onClick={clearAllRows}
                onMouseEnter={(e) => e.currentTarget.style.background = '#475569'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#64748b'}
              >
                {t("clearFormButton")}
              </button>
            </div>
          </form>

          {results && (
            <div style={styles.results}>
              <h3 style={styles.resultsH3}>📊 {t("resultsTitle")}</h3>

              <table style={styles.resultsTable}>
                <thead>
                  <tr>
                    <th style={styles.resultsTh}>{t("dayColumn")}</th>
                    <th style={styles.resultsTh}>{t("startColumn")}</th>
                    <th style={styles.resultsTh}>{t("endColumn")}</th>
                    <th style={styles.resultsTh}>{t("breakColumn")}</th>
                    <th style={styles.resultsTh}>{t("hoursColumn")}</th>
                    {includePayment && <th style={styles.resultsTh}>{t("payColumn")}</th>}
                  </tr>
                </thead>
                <tbody>
                  {results.dailyData.map((data: any, index: number) => (
                    <tr key={index}>
                      <td style={styles.resultsTd}>{t(`days.${data.day.toLowerCase()}`)}</td>
                      <td style={styles.resultsTd}>{data.start}</td>
                      <td style={styles.resultsTd}>{data.end}</td>
                      <td style={styles.resultsTd}>{data.break}</td>
                      <td style={styles.resultsTd}><strong>{formatHours(data.hours)}</strong></td>
                      {includePayment && <td style={styles.resultsTd}>${data.pay.toFixed(2)}</td>}
                    </tr>
                  ))}
                  <tr style={styles.totalRow}>
                    <td style={styles.resultsTd} colSpan={4}><strong>{t("totalLabel")}</strong></td>
                    <td style={styles.resultsTd}><strong>{formatHours(results.totalHours)}</strong></td>
                    {includePayment && <td style={styles.resultsTd}><strong>${results.totalPay.toFixed(2)}</strong></td>}
                  </tr>
                </tbody>
              </table>

              <div style={styles.resultSummary}>
                <div style={styles.summaryCard}>
                  <div style={styles.summaryLabel}>{t("totalHoursLabel")}</div>
                  <div style={styles.summaryValue}>{formatHours(results.totalHours)}</div>
                </div>
                {includePayment && (
                  <>
                    <div style={styles.summaryCard}>
                      <div style={styles.summaryLabel}>{t("regularHoursLabel")}</div>
                      <div style={styles.summaryValue}>{formatHours(results.totalRegularHours)}</div>
                    </div>
                    {results.totalOvertimeHours > 0 && (
                      <div style={styles.summaryCard}>
                        <div style={styles.summaryLabel}>{t("overtimeHoursLabel")}</div>
                        <div style={styles.summaryValue}>{formatHours(results.totalOvertimeHours)}</div>
                      </div>
                    )}
                    <div style={styles.summaryCard}>
                      <div style={styles.summaryLabel}>{t("totalPayLabel")}</div>
                      <div style={styles.summaryValue}>${results.totalPay.toFixed(2)}</div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("introTitle")}</h2>
        <p style={styles.paragraph}>{t("introParagraph1")}</p>
        <p style={styles.paragraph}>{t("introParagraph2")}</p>
        <p style={styles.paragraph}>{t("introParagraph3")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("howToUseTitle")}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t("step1")}</li>
          <li style={styles.listItem}>{t("step2")}</li>
          <li style={styles.listItem}>{t("step3")}</li>
          <li style={styles.listItem}>{t("step4")}</li>
          <li style={styles.listItem}>{t("step5")}</li>
          <li style={styles.listItem}>{t("step6")}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("insightsTitle")}</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("insight1")}</li>
          <li style={styles.listItem}>{t("insight2")}</li>
          <li style={styles.listItem}>{t("insight3")}</li>
          <li style={styles.listItem}>{t("insight4")}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("detailsTitle")}</h2>
        <p style={styles.paragraph}>{t("detailsParagraph1")}</p>
        <p style={styles.paragraph}>{t("detailsParagraph2")}</p>
        <p style={styles.paragraph}>{t("detailsParagraph3")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("faqTitle")}</h2>
        <div style={styles.faqItem}>
          <div style={styles.faqQuestion}>{t("faq1Question")}</div>
          <div style={styles.faqAnswer}>{t("faq1Answer")}</div>
        </div>
        <div style={styles.faqItem}>
          <div style={styles.faqQuestion}>{t("faq2Question")}</div>
          <div style={styles.faqAnswer}>{t("faq2Answer")}</div>
        </div>
        <div style={styles.faqItem}>
          <div style={styles.faqQuestion}>{t("faq3Question")}</div>
          <div style={styles.faqAnswer}>{t("faq3Answer")}</div>
        </div>
        <div style={styles.faqItem}>
          <div style={styles.faqQuestion}>{t("faq4Question")}</div>
          <div style={styles.faqAnswer}>{t("faq4Answer")}</div>
        </div>
        <div style={styles.faqItem}>
          <div style={styles.faqQuestion}>{t("faq5Question")}</div>
          <div style={styles.faqAnswer}>{t("faq5Answer")}</div>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("referencesTitle")}</h2>
        <div style={styles.reference}>
          <a href="https://www.beforesunset.ai/post/why-should-you-use-a-time-card-calculator" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
            {t("reference1")}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://uplandsoftware.com/articles/specialized-tech/what-is-a-time-card/" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
            {t("reference2")}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://attotime.com/blog/what-is-time-card" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
            {t("reference3")}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://www.monitask.com/en/blog/the-benefits-of-using-a-time-card-calculator-to-manage-your-work-hours" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
            {t("reference4")}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://empmonitor.com/blog/time-clock-calculator-in-your-business/" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
            {t("reference5")}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://desktrack.timentask.com/blog/time-card-calculator/" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
            {t("reference6")}
          </a>
        </div>
      </section>
    </div>
  );
}